import { useState, FormEvent, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { EyeIcon, EyeSlashIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { API_URL } from "../config";
import { authService } from '../services/authService';

// Types pour la réponse de l'API
interface LoginResponse {
  access_token: string;
  user: {
    role: string;
  };
}

interface ErrorResponse {
  error?: string;
}

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
  const token = localStorage.getItem("token_admin_silverjarz");
  if (token) {
    navigate("/admin/dashboard", { replace: true });
  }
}, [navigate]);

 const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
  e.preventDefault();
  setError("");
  setLoading(true);

  try {
    const response = await axios.post<LoginResponse>(`${API_URL}/login-admin`, {
      email,
      password,
    });

    // Utiliser le service pour stocker le token
    authService.setToken(
      response.data.access_token,
      response.data.user.role
    );

    navigate("/admin/dashboard");
  } catch (err: any) {
    if (err.response && err.response.data) {
      const errorData = err.response.data as ErrorResponse;
      setError(errorData.error || "Login failed");
    } else {
      setError("Server error");
    }
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      <style>{`
        /* ============================================
           Admin Login — Travel & Visa Platform
        ============================================ */

        .login-page {
          min-height: 100vh;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f4f1ea;
          padding: 40px 16px;
          font-family: "Helvetica Neue", Arial, sans-serif;
          box-sizing: border-box;
        }

        .login-card {
          width: 100%;
          max-width: 920px;
          display: grid;
          grid-template-columns: 0.95fr 1.1fr;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 20px 60px -15px rgba(29, 54, 64, 0.35);
          background: #ffffff;
        }

        .login-brand {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background-color: #1d3640;
          color: #f3ecd9;
          padding: 48px 40px;
        }

        .login-brand-top {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 56px;
        }

        .login-brand-logo-circle {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid #d3be76;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .login-brand-logo-circle img {
          height: 20px;
          width: auto;
        }

        .login-brand-label {
          font-size: 12px;
          letter-spacing: 2px;
          color: #d3be76;
          font-weight: 500;
          text-transform: uppercase;
        }

        .login-brand-title {
          font-family: Georgia, "Times New Roman", serif;
          font-size: 32px;
          line-height: 1.25;
          margin: 0 0 16px;
          color: #f7f5f0;
        }

        .login-brand-subtitle {
          font-size: 14px;
          color: #b8c4c8;
          line-height: 1.7;
          max-width: 300px;
          margin: 0;
        }

        .login-route {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 11px;
          letter-spacing: 1.5px;
          color: #d3be76;
          margin-bottom: 10px;
        }

        .login-route-line {
          flex: 1;
          height: 1px;
          background-image: repeating-linear-gradient(
            to right,
            #d3be76 0,
            #d3be76 4px,
            transparent 4px,
            transparent 8px
          );
        }

        .login-brand-footer-label {
          font-size: 11px;
          color: #7f9098;
          letter-spacing: 1px;
          text-transform: uppercase;
          margin: 0;
        }

        .login-brand::after {
          content: "";
          position: absolute;
          right: -1px;
          top: 0;
          bottom: 0;
          width: 14px;
          background-image: radial-gradient(
              circle 7px at 0 0,
              transparent 6px,
              #ffffff 6px
            ),
            radial-gradient(circle 7px at 0 100%, transparent 6px, #ffffff 6px);
          background-repeat: repeat-y;
          background-size: 14px 16px;
          background-position: left center;
        }

        .login-form-panel {
          background: #ffffff;
          padding: 48px 56px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .login-mobile-logo {
          display: none;
          justify-content: center;
          margin-bottom: 32px;
        }

        .login-mobile-logo img {
          height: 40px;
          width: auto;
        }

        .login-form-title {
          font-family: Georgia, serif;
          font-size: 24px;
          color: #1d3640;
          margin: 0 0 4px;
        }

        .login-form-subtitle {
          font-size: 13px;
          color: #8a8473;
          margin: 0 0 32px;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .login-error {
          font-size: 13px;
          color: #c0392b;
          background: #fdecea;
          border: 1px solid #f5c6c0;
          border-radius: 6px;
          padding: 10px 12px;
          text-align: center;
        }

        .login-field-label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.5px;
          color: #1d3640;
          margin-bottom: 6px;
        }

        /* SOLUTION : Styles avec !important pour forcer l'affichage */
        .login-input {
          width: 100% !important;
          height: 44px !important;
          border-radius: 6px !important;
          border: 2px solid #8a8473 !important;
          padding: 0 14px !important;
          font-size: 14px !important;
          color: #1d3640 !important;
          outline: none !important;
          background: #ffffff !important;
          box-sizing: border-box !important;
          transition: border-color 0.15s ease, box-shadow 0.15s ease !important;
        }

        .login-input::placeholder {
          color: #b0a89a;
          font-size: 13px;
        }

        .login-input:focus {
          border-color: #294b5a !important;
          box-shadow: 0 0 0 3px rgba(41, 75, 90, 0.12) !important;
        }

        .login-input-wrapper {
          position: relative;
        }

        .login-input-with-icon {
          padding-right: 40px !important;
        }

        .login-toggle-password {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          padding: 0;
          display: flex;
          align-items: center;
          cursor: pointer;
          color: #9a9484;
        }

        .login-toggle-password:hover {
          color: #294b5a;
        }

        .login-toggle-password svg {
          width: 20px;
          height: 20px;
        }

        .login-forgot-row {
          display: flex;
          justify-content: flex-end;
          margin-top: -8px;
        }

        .login-forgot-link {
          background: none;
          border: none;
          padding: 0;
          font-size: 12px;
          font-weight: 500;
          color: #b09a50;
          cursor: pointer;
          transition: color 0.15s ease;
        }

        .login-forgot-link:hover {
          color: #294b5a;
        }

        .login-submit {
          height: 48px;
          border-radius: 6px;
          background-color: #294b5a;
          color: #ffffff;
          border: none;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.3px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: background-color 0.15s ease, opacity 0.15s ease;
        }

        .login-submit:hover:not(:disabled) {
          background-color: #1d3640;
        }

        .login-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-footer-note {
          margin-top: 32px;
          padding-top: 20px;
          border-top: 1px solid #eee8da;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #9a9484;
        }

        .login-footer-note svg {
          width: 14px;
          height: 14px;
          color: #b09a50;
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .login-card {
            grid-template-columns: 1fr;
          }

          .login-brand {
            display: none;
          }

          .login-mobile-logo {
            display: flex;
          }

          .login-form-panel {
            padding: 40px 28px;
          }
        }
      `}</style>

      <section className="login-page">
        <div className="login-card">
          {/* Left brand panel */}
          <div className="login-brand">
            <div>
              <div className="login-brand-top">
                <div className="login-brand-logo-circle">
                  <img src="/img/logo-100.png" alt="Logo" />
                </div>
                <span className="login-brand-label">Admin Portal</span>
              </div>

              <h1 className="login-brand-title">
                Trips and visas,
                <br />
                managed with precision.
              </h1>
              <p className="login-brand-subtitle">
                Secure access for the Oman and MENA region operations team.
              </p>
            </div>

            <div>
              <div className="login-route">
                <span>MCT</span>
                <span className="login-route-line" aria-hidden="true" />
                <span>DXB</span>
              </div>
              <p className="login-brand-footer-label">
                Visa and trip operations
              </p>
            </div>
          </div>

          {/* Right form panel */}
          <div className="login-form-panel">
            <div className="login-mobile-logo">
              <img src="/img/logo-100.png" alt="Logo" />
            </div>

            <h2 className="login-form-title">Sign in</h2>
            <p className="login-form-subtitle">
              Enter your credentials to access the dashboard.
            </p>

            <form onSubmit={handleLogin} className="login-form">
              {error && <div className="login-error">{error}</div>}

              <div>
                <label className="login-field-label">Email</label>
                <input
                  type="email"
                  placeholder="name@omantravel.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="login-input"
                  style={{
                    width: '100%',
                    height: '44px',
                    borderRadius: '6px',
                    border: '2px solid #8a8473',
                    padding: '0 14px',
                    fontSize: '14px',
                    color: '#1d3640',
                    outline: 'none',
                    background: '#ffffff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label className="login-field-label">Password</label>
                <div className="login-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="login-input login-input-with-icon"
                    style={{
                      width: '100%',
                      height: '44px',
                      borderRadius: '6px',
                      border: '2px solid #8a8473',
                      padding: '0 40px 0 14px',
                      fontSize: '14px',
                      color: '#1d3640',
                      outline: 'none',
                      background: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="login-toggle-password"
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <div className="login-forgot-row">
                <button type="button" className="login-forgot-link">
                  Forgot password?
                </button>
              </div>

              <button type="submit" disabled={loading} className="login-submit">
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <div className="login-footer-note">
              <LockClosedIcon />
              Restricted access. Authorized personnel only.
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Login;